import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";

import { JsonConvert } from "json2typescript";

import { Observable, of, tap } from "rxjs";

import { catchError } from "rxjs/operators";

import {
    CustomerInformation,
    CustomerSessionResponse,
    Guest,
    User,
} from "@hermes/api-model-account";
import { Address } from "@hermes/api-model-address";
import { Basket, Fapiao, WebToShopDetails } from "@hermes/api-model-basket";
import {
    LoginResponse,
    UnionIdResponse,
    WechatBindingResponse,
} from "@hermes/api-model-session";
import {
    Context,
    Settings,
    StorageManager,
    StorageService,
} from "@hermes/core-app-core";

import { BasketFacade } from "@hermes/hermes-web-states-basket-state";
import { HTTP_NO_CACHE_CONTROL_HEADER } from "@hermes/utils-generic-constants";

import { UserFacade } from "../facades/user.facade";

@Injectable({ providedIn: "root" })
export class UserStateService {
    readonly #context = inject(Context);
    readonly #storageService = inject(StorageService);
    readonly #http = inject(HttpClient);
    readonly #settings = inject(Settings);
    readonly #userFacade = inject(UserFacade);
    readonly #basketFacade = inject(BasketFacade);

    private sessionStorage: StorageManager;
    private localStorage: StorageManager;

    public readonly availableCountryCodes = signal<string[]>([]);

    constructor() {
        this.sessionStorage =
            this.#storageService.getSessionStorageInstance() as StorageManager;
        this.localStorage =
            this.#storageService.getLocalStorageInstance() as StorageManager;
    }

    public fetchCustomerSession(forceRefresh = true) {
        if (this.#userFacade.userIsSynchronized() && !forceRefresh) {
            return of(
                Object.assign(new CustomerSessionResponse(), {
                    user: this.#userFacade.userDetails(),
                    basket: this.#basketFacade.basket(),
                }),
            );
        }

        return this.#http
            .get<CustomerSessionResponse>(
                `${this.#settings.apiUrl}/customer-session`,
                {
                    headers: new HttpHeaders({
                        ...HTTP_NO_CACHE_CONTROL_HEADER,
                    }),
                },
            )
            .pipe(
                tap((customerSession) => {
                    if (customerSession.user) {
                        this.loginUser(customerSession.user);
                    } else {
                        this.#userFacade.fetchGuest();
                    }

                    if (customerSession.basket) {
                        this.#basketFacade.setBasket(customerSession.basket);
                    }
                }),
                catchError((error) => {
                    throw new Error(
                        `fetch customer session error : ${error.error}`,
                    );
                }),
            );
    }

    /**
     * Retrieve the basket from sessionStorage.
     * Return an empty basket if not found
     */
    public getBasket(): Basket {
        let user: { basket: Basket } | undefined =
            this.getSessionStorageData("user");
        if (user) {
            user = new JsonConvert().deserializeObject(user, User);
            return user.basket ?? new Basket();
        }
        return new Basket();
    }

    public getCustomerInformation(
        isLightRequest: boolean = false,
    ): Observable<CustomerInformation> {
        const httpOptions = {
            params: new HttpParams().set("isLightRequest", isLightRequest),
        };
        return this.#http
            .get<CustomerInformation>(
                `${this.#settings.apiUrl}/customer/information`,
                httpOptions,
            )
            .pipe(
                catchError((error) => {
                    throw error;
                }),
            );
    }

    public getUser(): User | undefined {
        const user = this.#userFacade.userDetails();
        if (!user.email) {
            return undefined;
        }

        return user;
    }

    /**
     * Clear User in Session Storage
     */
    public clearUser(): void {
        this.removeSessionStorageData("user");
    }

    public sessionNeedsRefresh(): boolean {
        return this.getSessionStorageData("sessionNeedsRefresh") === true;
    }

    public setSessionNeedsRefresh(sessionNeedsRefresh: boolean): void {
        this.setSessionStorageData("sessionNeedsRefresh", sessionNeedsRefresh);
    }

    /**
     * @description Log in the given user
     */
    public loginUser(user: User): void {
        this.#userFacade.removeGuest();
        this.#userFacade.setUser(user);
    }

    /**
     * Called when the login is successful for a returning customer
     */
    public deprecatedLoginSuccess(
        response: LoginResponse | UnionIdResponse | WechatBindingResponse,
    ): void {
        this.#userFacade.removeGuest();
        // sets the shipping addresses in the localstorage
        if (response?.shippingAddresses) {
            this.setShippingAdresses(response.shippingAddresses);
        }
        // sets the available countries in the localstorage
        if (response.availableCountries) {
            this.setAvailableCountries(response.availableCountries);
        }

        if (response.user) {
            this.loginUser(response.user);
        }
    }

    public getGuest(): Guest {
        return this.getSessionStorageData("guest") as Guest;
    }

    public removeCheckout(): void {
        this.removeLocalStorageData("checkout");
    }

    public setCheckoutStep(step: unknown): void {
        const checkout = this.getLocalStorageData<{ step: unknown }>(
            "checkout",
        );

        if (checkout) {
            checkout.step = step;
            this.setLocalStorageData("checkout", checkout);
        } else {
            this.setLocalStorageData("checkout", {
                step,
            });
        }
    }

    public getCheckoutStep(): unknown {
        const checkout = this.getLocalStorageData<{ step: unknown }>(
            "checkout",
        );

        if (checkout && checkout.step) {
            return checkout.step;
        }
        return undefined;
    }

    public setShippingAdresses(addresses: Address[]): void {
        const checkout = this.getLocalStorageData<{
            shippingAddresses: Address[];
        }>("checkout");

        if (checkout) {
            checkout.shippingAddresses = addresses;
            this.setLocalStorageData("checkout", checkout);
        } else {
            this.setLocalStorageData("checkout", {
                shippingAddresses: addresses,
            });
        }
    }

    public getShippingAddresses(): Address[] {
        const checkout = this.getLocalStorageData<{
            shippingAddresses: Address[];
        }>("checkout");
        let shippingAddresses: Address[] = [];

        if (checkout && checkout.shippingAddresses) {
            shippingAddresses = checkout.shippingAddresses;
        }
        return shippingAddresses;
    }

    public setBillingAddresses(addresses: Address[]): void {
        const checkout = this.getLocalStorageData<{
            billingAddresses: Address[];
        }>("checkout");

        if (checkout) {
            checkout.billingAddresses = addresses;
            this.setLocalStorageData("checkout", checkout);
        } else {
            this.setLocalStorageData("checkout", {
                billingAddresses: addresses,
            });
        }
    }

    public getBillingAddresses(): Address[] {
        const checkout = this.getLocalStorageData<{
            billingAddresses: Address[];
        }>("checkout");
        let billingAddresses: Address[] = [];

        if (checkout && checkout.billingAddresses) {
            billingAddresses = checkout.billingAddresses;
        }
        return billingAddresses;
    }

    public setAvailableCountries(countries: string[]): void {
        const checkout = this.getLocalStorageData<{
            availableCountries: string[];
        }>("checkout");

        if (checkout) {
            checkout.availableCountries = countries;
            this.setLocalStorageData("checkout", checkout);
        } else {
            this.setLocalStorageData("checkout", {
                availableCountries: countries,
            });
        }

        this.availableCountryCodes.set(countries);
    }

    public getAvailableCountries(): string[] {
        const checkout = this.getLocalStorageData<{
            availableCountries: string[];
        }>("checkout");
        let availableCountries: string[] = [];

        if (checkout && checkout.availableCountries) {
            availableCountries = checkout.availableCountries;
        }

        return availableCountries;
    }

    public setShops(shops: WebToShopDetails[]): void {
        const checkout = this.getLocalStorageData<{
            shops: WebToShopDetails[];
        }>("checkout");

        if (checkout) {
            checkout.shops = shops;
            this.setLocalStorageData("checkout", checkout);
        } else {
            this.setLocalStorageData("checkout", {
                shops,
            });
        }
    }

    public getShops(): WebToShopDetails[] {
        const checkout = this.getLocalStorageData<{
            shops: WebToShopDetails[];
        }>("checkout");
        let shops: WebToShopDetails[] = [];

        if (checkout && checkout.shops) {
            shops = checkout.shops;
        }
        return shops;
    }

    public setSelectedShop(shop: WebToShopDetails): void {
        const checkout = this.getLocalStorageData<{
            selectedShop: WebToShopDetails;
        }>("checkout");

        if (checkout) {
            checkout.selectedShop = shop;
            this.setLocalStorageData("checkout", checkout);
        } else {
            this.setLocalStorageData("checkout", {
                shop,
            });
        }
    }

    public removeSelectedShop(): void {
        const checkout = this.getLocalStorageData<{
            selectedShop?: WebToShopDetails;
        }>("checkout");
        if (checkout) {
            delete checkout["selectedShop"];
            this.setLocalStorageData("checkout", checkout);
        }
    }

    public getSelectedShop(): WebToShopDetails | undefined {
        const checkout = this.getLocalStorageData<{
            selectedShop: WebToShopDetails;
        }>("checkout");

        if (checkout && checkout.selectedShop) {
            return checkout.selectedShop;
        }
        return undefined;
    }

    public setLastShop(shopId: string): void {
        const checkout = this.getLocalStorageData<{ lastWebToShop: string }>(
            "checkout",
        );

        if (checkout) {
            checkout.lastWebToShop = shopId;
            this.setLocalStorageData("checkout", checkout);
        } else {
            this.setLocalStorageData("checkout", {
                shopId,
            });
        }
    }

    public getLastShop(): string | undefined {
        const checkout = this.getLocalStorageData<{ lastWebToShop: string }>(
            "checkout",
        );

        if (checkout && checkout.lastWebToShop) {
            return checkout.lastWebToShop;
        }
        return undefined;
    }

    public setFapiao(fapiao: Fapiao): void {
        const checkout = this.getLocalStorageData<{ fapiao: Fapiao }>(
            "checkout",
        );

        if (checkout) {
            checkout.fapiao = fapiao;
            this.setLocalStorageData("checkout", checkout);
        } else {
            this.setLocalStorageData("checkout", {
                fapiao,
            });
        }
    }

    public getFapiao(): Fapiao | undefined {
        const checkout = this.getLocalStorageData<{ fapiao: Fapiao }>(
            "checkout",
        );

        if (checkout && checkout.fapiao) {
            return checkout.fapiao;
        }
        return undefined;
    }

    public setCookieExpiration(): void {
        const checkout = this.getLocalStorageData<{
            cookieExpirationTime: number;
        }>("checkout");

        const expiration = this.getCookieExpirationFromSessionStorage();

        if (checkout) {
            checkout.cookieExpirationTime = expiration;
            this.setLocalStorageData("checkout", checkout);
        } else {
            this.setLocalStorageData("checkout", {
                cookieExpirationTime: expiration,
            });
        }
    }

    public getCookieExpirationFromLocalStorage(): number | undefined {
        const checkout = this.getLocalStorageData<{
            cookieExpirationTime: number;
        }>("checkout");

        if (checkout && checkout.cookieExpirationTime) {
            return checkout.cookieExpirationTime;
        }
        return undefined;
    }

    public getCookieExpirationFromSessionStorage(): number {
        return this.getSessionStorageData("cookieEndOfTime") || 0;
    }

    public getProductThreshold(): boolean | undefined {
        return this.getSessionStorageData("has_product_threshold");
    }

    private getSessionStorageData<S>(key: string): S | undefined {
        return this.#context.isInBrowserMode()
            ? this.sessionStorage.getItem<S>(key)
            : undefined;
    }

    private setSessionStorageData<S>(key: string, data: S): void {
        if (this.#context.isInBrowserMode()) {
            this.sessionStorage.setItem<S>(key, data);
        }
    }

    private getLocalStorageData<S>(key: string): S | undefined {
        return this.#context.isInBrowserMode()
            ? this.localStorage.getItem<S>(key)
            : undefined;
    }

    private setLocalStorageData<S>(key: string, data: S): void {
        if (this.#context.isInBrowserMode()) {
            this.localStorage.setItem<S>(key, data);
        }
    }

    private removeLocalStorageData(key: string): void {
        if (this.#context.isInBrowserMode() && this.getLocalStorageData(key)) {
            this.localStorage.deleteItem(key);
        }
    }

    private removeSessionStorageData(key: string): void {
        if (
            this.#context.isInBrowserMode() &&
            this.getSessionStorageData(key)
        ) {
            this.sessionStorage.deleteItem(key);
        }
    }
}
